import { forwardRef, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Pip } from "@pilot-protocol/theme/chrome";
import { cn } from "./lib/cn.js";

/**
 * Chip — the scifi status indicator.
 *
 * Sharp corners, hairline border in the tone color, monospace caps with
 * --pp-tracking-caps. Use for live state, league tags, provisional
 * flags — anywhere you'd see `[LIVE]` in a terminal UI.
 *
 * Wraps `<Pip>` from the shared chrome layer when `dot` is set so the
 * indicator pulse matches the rest of the app's pip vocabulary.
 */
const chipVariants = cva(
  [
    "inline-flex items-center gap-1.5 px-2 py-0.5",
    "border bg-transparent",
    "font-[var(--pp-font-family)] font-[var(--pp-weight-bold)] uppercase tracking-[var(--pp-tracking-caps)] leading-none",
    "text-[var(--pp-font-sm)] whitespace-nowrap",
  ],
  {
    variants: {
      tone: {
        default: "border-[var(--pp-text)] text-[var(--pp-text)]",
        accent: "border-[var(--accent)] text-[var(--accent)]",
        muted: "border-[var(--pp-text-muted)] text-[var(--pp-text-muted)]",
        error: "border-[var(--pp-danger)] text-[var(--pp-danger)]",
        warn: "border-[var(--pp-warn)] text-[var(--pp-warn)]",
      },
    },
    defaultVariants: {
      tone: "default",
    },
  },
);

export type ChipTone = "default" | "accent" | "muted" | "error" | "warn";

export interface ChipProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {
  tone?: ChipTone;
  /** Show a leading status dot. Useful for `[● LIVE]`-style chips. */
  dot?: boolean;
}

function dotColor(tone?: ChipTone): string {
  switch (tone) {
    case "accent":
      return "var(--accent)";
    case "error":
      return "var(--pp-danger)";
    case "warn":
      return "var(--pp-warn)";
    case "muted":
      return "var(--pp-text-muted)";
    default:
      return "var(--pp-text)";
  }
}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, tone, dot, children, ...props }, ref) => (
    <span
      ref={ref}
      data-pp-chip
      data-pp-tone={tone ?? "default"}
      className={cn(chipVariants({ tone }), className)}
      {...props}
    >
      {dot ? <Pip color={dotColor(tone)} filled size={6} glow={tone === "accent"} /> : null}
      {children}
    </span>
  ),
);
Chip.displayName = "Chip";

export { chipVariants };
